import { useCallback, useState } from 'react';
import {
  chosenFromKeys,
  clearChoices,
  lendingDecision,
  preselectRequired,
  type LendableState,
  type LendingDecision,
} from './app-lending-flow';
import { useLendableConnections } from './use-lendable-connections';
import { queryClient, queryKeys } from './query-client';
import { revokeAppShare, shareApp, useAppShares, type AppSharesState } from './app-shares';
import { DEFAULT_DATA_ACCESS, type DataAccess } from './app-data-access';
import { confirmAppliesTo, planGrants, releaseRest, type FirstGrantOutcome } from './recipient-field';
import {
  cancelConfirm,
  initialShareDialogState,
  removeShare,
  shareNotices,
  submitShare,
  type GrantOutcome,
  type ManyGrantResult,
  type ShareAttempt,
  type ShareDialogState,
  type ShareFlowDeps,
} from './share-flow';

// Binds the framework-free share flow (lib/share-flow.ts) to React state and the real data layer.
// Everything that can be decided without a DOM lives there and is tested there; this file is the wiring
// only — which is why it holds no branching of its own beyond `enabled`.

export interface AppShareFlow {
  state: ShareDialogState;
  /** The people list. Its `error` branch is what the dialog renders as "couldn't load", never as private. */
  shares: AppSharesState;
  /**
   * Retire the last attempt's verdict, without touching an open question.
   *
   * Replaces the old `setEmail`, which the recipient field made dead: the dialog no longer holds one
   * address, so "the owner is typing a new one" is now "the owner edited the list". The QUESTION
   * deliberately survives — `confirmAppliesTo` is what keeps a stored yes honest across an edit, and
   * destroying the question instead is the bug both phones had.
   */
  clearError: () => void;
  /** `confirmUnknown` answers the "nobody answers to that address" question with yes. */
  submit: (confirmUnknown?: boolean) => Promise<void>;
  /**
   * Share with SEVERAL people in one press, and answer with the ones that did NOT land — so the
   * dialog can leave exactly those on screen instead of asking the owner to remember.
   */
  submitMany: (identifiers: string[], confirmUnknown?: boolean) => Promise<ManyGrantResult>;
  /** The owner said no. Nothing was written, so this only clears the question. */
  cancelConfirm: () => void;
  remove: (granteeRecipientId: string) => Promise<void>;
  /** The retry offered under a FAILED load — re-runs the read, nothing else. */
  retry: () => void;

  // ---- lending: what this share lets the grantee do on the OWNER'S account, if anything ----

  /** Off by default. An ordinary share reaches nobody but the person who opens it. */
  lending: boolean;
  setLending: (lending: boolean) => void;
  /** The owner's connections, read only once `lending` is on. */
  lendable: LendableState;
  /** The ticked tools, as opaque keys — `toolKey`/`chosenFromKeys` own their shape. */
  chosen: ReadonlySet<string>;
  /** Whether the owner has asked past the app's declaration to every tool they have. */
  showAllTools: boolean;
  setShowAllTools: (showAll: boolean) => void;
  setChosen: (chosen: Set<string>) => void;
  /** Whether Share may fire, and why not when not. */
  decision: LendingDecision;

  // ---- hosted apps: what the person may change in the app's shared data ----

  /** EDIT_ALL by default. Sent only for a hosted app; an ordinary app has no data in Symphony to scope. */
  dataAccess: DataAccess;
  setDataAccess: (access: DataAccess) => void;
}

export function useAppShare(appId: string | undefined, requiredTools?: string[], hosted = false): AppShareFlow {
  const [state, setState] = useState<ShareDialogState>(initialShareDialogState);
  const [dataAccess, setDataAccess] = useState<DataAccess>(DEFAULT_DATA_ACCESS);
  const [lending, setLendingRaw] = useState(false);
  const [chosen, setChosen] = useState<ReadonlySet<string>>(new Set());
  /** Whether the owner asked past the app's own declaration to the full list. */
  const [showAllTools, setShowAllTools] = useState(false);
  /** So the pre-tick runs once per switch-on, and never re-ticks what the owner has just unticked. */
  const [preselected, setPreselected] = useState(false);
  const shares = useAppShares(appId);
  const lendable = useLendableConnections(lending);

  const reload = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.appShares(appId) });
  }, [appId]);

  const deps: ShareFlowDeps = {
    share: async (id, email, confirmUnknown) => {
      const outcome = await shareApp(id, email, hosted ? [] : chosenFromKeys(chosen), confirmUnknown, { hosted, dataAccess });
      // Every field passed through, none swallowed. `requiresConfirmation` in particular MUST reach
      // the flow: it means nothing was written, and a caller that drops it reports a share that
      // never happened.
      return {
        granteeIsNew: outcome.share?.granteeIsNew === true,
        requiresConfirmation: outcome.requiresConfirmation,
        granteeNotified: outcome.granteeNotified,
        alreadyShared: outcome.alreadyShared,
        shareLink: outcome.shareLink,
      };
    },
    revoke: async (id, granteeRecipientId) => {
      await revokeAppShare(id, granteeRecipientId);
    },
    setState,
    reload,
  };

  // Pre-tick ONLY the tools the app itself declared, and only once the list they must be matched
  // against has actually loaded. An app that declared nothing seeds nothing, exactly as before — the
  // one screen where a forgotten tick hands somebody else the keys is not the place to be generous.
  if (lending && !preselected && lendable.status === 'ready' && (requiredTools?.length ?? 0) > 0) {
    const seed = preselectRequired(lendable.connections, requiredTools);
    setPreselected(true);
    if (seed.size > 0) setChosen(seed);
  }

  // One recipient's grant, with its own notices captured. `submitShare` writes the single-attempt
  // state as it always has (so a one-person share is untouched); the spy sits on the RPC result and
  // reads it through the SAME `shareNotices` derivation, so the two cannot disagree.
  const grantOne = async (
    identifier: string,
    base: ShareDialogState,
    confirmUnknown: boolean,
  ): Promise<{ attempt: ShareAttempt; outcome: GrantOutcome | null }> => {
    let outcome: GrantOutcome | null = null;
    const spy: ShareFlowDeps = {
      ...deps,
      share: async (id, email, confirm) => {
        const result = await deps.share(id, email, confirm);
        if (!result?.requiresConfirmation) outcome = { identifier: email, ...shareNotices(result) };
        return result;
      },
    };
    const attempt = await submitShare(appId ?? '', { ...base, email: identifier }, spy, confirmUnknown);
    return { attempt, outcome: attempt === 'shared' ? outcome : null };
  };

  return {
    state,
    shares,
    lending,
    // Turning lending OFF forgets the ticks rather than remembering them. A remembered list is a share
    // that lends something the person stopped looking at.
    setLending: (next: boolean) => {
      setLendingRaw(next);
      if (!next) {
        setChosen(clearChoices());
        setShowAllTools(false);
        setPreselected(false);
      }
    },
    showAllTools,
    setShowAllTools: (next: boolean) => setShowAllTools(next),
    dataAccess,
    setDataAccess: (next: DataAccess) => setDataAccess(next),
    lendable,
    chosen,
    setChosen: (next: Set<string>) => setChosen(next),
    decision: lendingDecision({ lending, chosen, emailValid: true }),
    clearError: () => setState((prev) => (prev.error ? { ...prev, error: null } : prev)),
    submit: async (confirmUnknown = false) => {
      if (!appId) return;
      // The server refuses lending-with-nothing-ticked, so firing it would report a failure for a
      // mistake the dialog can already see. Same rule, one round trip earlier.
      if (!lendingDecision({ lending, chosen, emailValid: true }).canShare) return;
      const attempt = await submitShare(appId, state, deps, confirmUnknown);
      // Reset ONLY on a share that actually happened. The next share starts from nothing lent —
      // carrying a selection over is how one person's grant silently becomes the next person's.
      // On the unknown-address QUESTION nothing was written and the owner is about to answer it, so
      // clearing their ticks here would either lose the grant they chose or, worse, let the retry go
      // through with less lent than the screen still shows.
      if (attempt === 'shared') {
        setLendingRaw(false);
        setChosen(clearChoices());
      }
    },
    submitMany: async (identifiers, confirmUnknown = false) => {
      if (!appId) return { failed: identifiers, outcomes: [] };
      // The server refuses lending-with-nothing-ticked, so firing it would report a failure for a
      // mistake the dialog can already see.
      if (!lendingDecision({ lending, chosen, emailValid: true }).canShare) return { failed: identifiers, outcomes: [] };
      const plan = planGrants(identifiers, 'app', appId);
      if (!plan) return { failed: [], outcomes: [] };

      // The stored yes is honoured only while the run still BEGINS with the address it was given
      // about; the list stays editable under the question, and a transferred yes bypasses the
      // unknown-address gate for somebody nobody asked about.
      const confirmed = confirmUnknown && confirmAppliesTo(state.confirmEmail, plan.first);
      const first = await grantOne(plan.first, state, confirmed);
      const outcome: FirstGrantOutcome =
        first.attempt === 'shared' ? 'wrote' : first.attempt === 'question' ? 'askedFirst' : 'failed';
      const outcomes = first.outcome ? [first.outcome] : [];
      // A question or a failure stops the run where it is: the rest have not been attempted, so they
      // are all still owed. Whatever the owner answers resumes from this same list.
      if (!releaseRest(plan, outcome) || outcome !== 'wrote') return { failed: identifiers, outcomes };

      for (let i = 0; i < plan.rest.length; i += 1) {
        const next = await grantOne(plan.rest[i], initialShareDialogState, false);
        if (next.outcome) outcomes.push(next.outcome);
        // The FIRST stumble stops the run — from here on nothing has been attempted, so everything
        // from it onward stays on screen rather than being reported as done.
        if (next.attempt !== 'shared') return { failed: plan.rest.slice(i), outcomes };
      }
      // Only once every grant landed. Carrying a selection over is how one person's grant silently
      // becomes the next person's — and it has not been carried over until the run is finished.
      setLendingRaw(false);
      setChosen(clearChoices());
      return { failed: [], outcomes };
    },
    cancelConfirm: () => cancelConfirm(state, deps),
    remove: async (granteeRecipientId: string) => {
      if (!appId) return;
      await removeShare(appId, granteeRecipientId, state, deps);
    },
    retry: reload,
  };
}
